/* Plakat-Zoom — 1:1-Portierung des abgenommenen Prototyps
   Quelle: Vorwissen/Journal Plakat/Mood-Plakat/Zoom-Prototyp/mood-zoom-prototyp.html

   Kamera-Logik, Easing (easeInOutCubic), Tempo und Rand (MARGIN) sind
   unveraendert uebernommen. Geaendert wurde nur, was die Portierung
   zwingend verlangt:

   1. Scroll-Nullpunkt: Der Prototyp lag ganz oben auf der Seite und
      rechnete mit window.scrollY. In der Section steht darueber die
      Produktinfo, also wird der Track-Offset abgezogen.
   2. Horizon scrollt ab 990px nicht im window, sondern in .page-wrapper
      (html/body bekommen dort overflow:hidden). Der Scroller wird
      deshalb ermittelt und beim Resize neu gebunden.
   3. Buehne beginnt unter dem klebenden Header, Beschriftung ueber der
      Kaufleiste — beides misst das Theme, nicht der Prototyp.
   4. Alles ueber das Section-Root statt feste IDs, damit mehrere
      Instanzen moeglich sind.

   LEISTUNG — wichtig beim Aendern:
   Im Scroll-Pfad wird ausschliesslich die Scroll-Position GELESEN und
   die viewBox (plus Overlays) GESCHRIEBEN. Jede Layout-Messung
   (getBoundingClientRect, offsetHeight/Width, getComputedStyle,
   querySelectorAll ueber das Dokument) erzwingt bei einem 1,9-MB-SVG
   eine teure Neuberechnung und gehoert NICHT in diesen Pfad. Alle
   Geometriewerte werden einmal beim Init gemessen und danach nur noch
   bei resize, Groessenaenderung (ResizeObserver) und beim Ein-/
   Ausblenden der Kaufleiste (transitionend/scrollend) aufgefrischt.
*/

/* Das SVG wird zur Laufzeit geholt und als echter Vektor in den DOM
   gehaengt — nicht als <img>, sonst waere die viewBox nicht steuerbar.

   Warum nicht per Liquid inline, wie urspruenglich vorgesehen:
   {{ ... | inline_asset_content }} ist auf 15 KB begrenzt, Liquid-Dateien
   (Snippets) auf 256 KB. Das Plakat-SVG hat 1,9 MB — beide Wege sind
   von der Plattform her ausgeschlossen, unabhaengig vom Theme.
   Als Theme-Asset gilt diese Grenze nicht. Nebeneffekt: die 1,9 MB
   liegen nicht im HTML, werden separat vom Shopify-CDN gecacht und
   erst geladen, wenn die Section in Sichtweite kommt. */
async function loadPoster(root, stage) {
  const url = root.dataset.pzSvg;
  if (!url) return null;

  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) throw new Error('Plakat-Zoom: SVG nicht erreichbar (' + res.status + ')');

  const doc = new DOMParser().parseFromString(await res.text(), 'image/svg+xml');
  if (doc.querySelector('parsererror') || doc.documentElement.nodeName !== 'svg') {
    throw new Error('Plakat-Zoom: SVG konnte nicht geparst werden');
  }

  const svg = document.importNode(doc.documentElement, true);
  /* Groesse kommt aus dem CSS, nicht aus den Attributen der Datei. */
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  /* Das SVG kommt in einen Shadow-Tree — aus Leistungsgruenden, nicht
     aus Kapselungsgruenden:

     Horizons Header schreibt bei JEDEM Scroll-Frame das Attribut
     data-scroll-direction auf sich selbst. Das invalidiert dokumentweit
     die Stile. Liegen die ~10.700 Knoten dieses Plakats im Dokument,
     kostet jede dieser Invalidierungen rund 50ms — der Scroll ruckelt
     massiv. Selektoren des Dokuments greifen nicht in einen Shadow-Tree,
     dadurch faellt der Stil-Neuaufbau von ~3400ms auf ~340ms je 5s.

     Die Groessenangaben muessen deshalb hier drin stehen; die Regeln aus
     plakat-zoom.css erreichen den Teilbaum nicht mehr. */
  if (stage.attachShadow) {
    const host = document.createElement('div');
    host.className = 'plakat-zoom__poster';
    const sr = host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent =
      ':host{display:block;width:100%;height:100%}svg{width:100%;height:100%;display:block}';
    sr.appendChild(style);
    sr.appendChild(svg);
    stage.appendChild(host);
  } else {
    stage.appendChild(svg);
  }
  return svg;
}

async function initPlakatZoom(root) {
  if (root.dataset.pzReady) return;
  root.dataset.pzReady = 'loading';

  const stage = root.querySelector('[data-pz-stage]');
  const track = root.querySelector('[data-pz-track]');
  const caption = root.querySelector('[data-pz-caption]');
  const hint = root.querySelector('[data-pz-hint]');
  const dotsBox = root.querySelector('[data-pz-dots]');
  const overlays = root.querySelector('[data-pz-overlays]');
  const dataEl = root.querySelector('[data-pz-stations]');

  if (!stage || !track || !caption || !hint || !dotsBox || !overlays || !dataEl) return;

  /* Plakat-Format (viewBox aller drei SVGs). Nur fuer die Vorpositionierung
     des Hinweises VOR dem SVG-Load gebraucht; nach dem Load wird die echte
     viewBox verwendet — beide Werte sind identisch, daher kein Springen. */
  const POSTER_W = 2385.12;
  const POSTER_H = 2835.12;

  /* Viewport-Position (top) des Hinweises, einstellbar im gruenen Streifen
     ZWISCHEN Header-Unterkante und Plakat-Oberkante. hintPositionPercent
     regelt die Lage: 0 = Header, 50 = Mitte, 100 = Plakat-Oberkante.
     Die Plakat-Oberkante ergibt sich aus der Intro-viewBox (Gesamtansicht
     mit Rand), gerechnet wie fit() — dadurch identisch auf allen Templates
     und relativ zum Plakat, nicht zum Viewport-Boden. */
  function hintTopFor(W, H, margin, sw, sh, header, hintPositionPercent = 50) {
    const w0 = W * margin;
    const h0 = H * margin;
    const va = sw / sh;
    const vbH = w0 / h0 < va ? h0 : w0 / va; /* finale Intro-viewBox-Hoehe */
    const posterTop = sh * (1 - H / vbH) / 2; /* Plakat-Oberkante, relativ zur Buehne */
    /* A = Header-Unterkante (absolut)
       B = Plakat-Oberkante (absolut, relativ zur Bühne)
       Hinweis-Mitte sitzt bei (A + (B - A) * percent/100), also zwischen A und B. */
    const B = posterTop; /* Plakat-Oberkante relativ zur Bühne (gleich wie trackTop) */
    const availableSpace = B - 0; /* Raum zwischen Stage-Top (0) und Plakat-Oberkante */
    return availableSpace * (hintPositionPercent / 100); /* Position relativ zur Stage-Top */
  }

  /* --- Scroll-Hinweis-Bruecke ---------------------------------------------
     Der Hinweis ist per CSS von Anfang an sichtbar. Diese Bruecke laeuft
     schon VOR dem ~1s dauernden SVG-Load: sie positioniert ihn im gruenen
     Streifen ueber dem Plakat und blendet ihn aus, falls die Seite mit
     wiederhergestellter Scrollposition mitten in der Fahrt landet oder
     waehrend des Ladens gescrollt wird — so blitzt der Hinweis nie auf.

     Fortschritt/Header werden viewport-relativ berechnet (track-Rechteck +
     Header-Hoehe), weil stage.style.top erst nach dem Load gesetzt wird. Ein
     einzelner capture-Listener am window erfasst window- UND .page-wrapper-
     Scroll. Nach dem Init uebernimmt update()/measure() die Steuerung; die
     Bruecke wird entfernt. */
  function stickyHeaderHeight() {
    let header = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => {
      const pos = getComputedStyle(el).position;
      if (pos === 'sticky' || pos === 'fixed') header += el.getBoundingClientRect().height;
    });
    return header;
  }
  function earlyHint() {
    const header = stickyHeaderHeight();
    const stageH = window.innerHeight - header;
    const m = track.offsetHeight - stageH;
    const rel = header - track.getBoundingClientRect().top;
    const p = m > 0 ? Math.min(Math.max(rel / m, 0), 1) : 1;
    /* Sichtbar von "Plakat gerade im Blick" (rel bis zu einer Buehnenhoehe
       vor dem Fahrt-Nullpunkt) bis 2% Fahrt. rel < -stageH: Section weit unten. */
    hint.classList.toggle('is-hidden', !(rel > -stageH && p < 0.02));
    /* Before Load: Fallback auf geometrische Mitte zwischen Header und Track-Top */
    const trackTop = track.getBoundingClientRect().top + window.scrollY;
    const hintPercent = (parseFloat(root.dataset.pzHintPosition) || 50) / 100;
    const midpoint = (header + trackTop) / 2 - trackTop;
    hint.style.setProperty('--pz-hint-y', midpoint + 'px');
  }
  let earlyTick = false;
  function onEarlyHint() {
    if (earlyTick) return;
    earlyTick = true;
    requestAnimationFrame(() => {
      earlyTick = false;
      earlyHint();
    });
  }
  addEventListener('scroll', onEarlyHint, { passive: true, capture: true });
  earlyHint();

  let svgEl;
  try {
    svgEl = await loadPoster(root, stage);
  } catch (err) {
    console.error(err);
    root.dataset.pzReady = 'error';
    return;
  }
  if (!svgEl) {
    root.dataset.pzReady = 'error';
    return;
  }

  /* Gesamtansicht kommt aus der viewBox des SVG selbst — erster und
     letzter Keyframe sind daher implizit, ohne Block. */
  const vb = (svgEl.getAttribute('viewBox') || '').trim().split(/[\s,]+/).map(Number);
  if (vb.length !== 4 || vb.some((n) => !isFinite(n))) return;
  const FULL = vb;

  let stations;
  try {
    stations = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }
  if (!Array.isArray(stations) || stations.length === 0) return;

  /* Stationen: [x, y, Breite, Hoehe] in viewBox-Einheiten + Beschriftung. */
  const ST = [{ r: FULL, t: root.dataset.pzIntro || '' }]
    .concat(stations)
    .concat([{ r: FULL, t: root.dataset.pzOutro || '' }]);

  const MARGIN = (parseFloat(root.dataset.pzMargin) || 110) / 100; /* Luft um jede Station */

  for (let i = 0; i < ST.length; i++) dotsBox.appendChild(document.createElement('i'));
  const dots = dotsBox.children;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }
  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  /* Kamera-Interpolation: Zentrum linear, Groesse logarithmisch
     (gleichmaessiges Zoomgefuehl) */
  function camRect(p) {
    const segs = ST.length - 1;
    const f = Math.min(Math.max(p, 0), 1) * segs;
    const i = Math.min(Math.floor(f), segs - 1);
    const t = reduce ? Math.round(f - i) : easeInOutCubic(f - i);
    const a = ST[i].r;
    const b = ST[i + 1].r;
    const cx = lerp(a[0] + a[2] / 2, b[0] + b[2] / 2, t);
    const cy = lerp(a[1] + a[3] / 2, b[1] + b[3] / 2, t);
    const w = Math.exp(lerp(Math.log(a[2]), Math.log(b[2]), t));
    const h = Math.exp(lerp(Math.log(a[3]), Math.log(b[3]), t));
    return [cx, cy, w, h];
  }

  /* Rechteck mit Rand auf das Seitenverhaeltnis der Buehne bringen.
     Im Prototyp war die Buehne exakt das Fenster — dort ist das identisch. */
  function fit(c) {
    const cx = c[0];
    const cy = c[1];
    let w = c[2] * MARGIN;
    let h = c[3] * MARGIN;
    const va = stageW / stageH;
    if (w / h < va) w = h * va;
    else h = w / va;
    return (
      (cx - w / 2).toFixed(2) + ' ' + (cy - h / 2).toFixed(2) + ' ' + w.toFixed(2) + ' ' + h.toFixed(2)
    );
  }

  /* ------------------------------------------------------------------
     Gemessene Geometrie — wird gecacht und NUR ausserhalb des
     Scroll-Pfads aufgefrischt.
     ------------------------------------------------------------------ */
  let scroller = null;
  let stickyTop = 0;
  let stageW = 1;
  let stageH = 1;
  let trackTop = 0;
  let max = 0;
  let headerH = 0;
  let lastHeader = -1;
  let lastFooter = -1;

  function detectScroller() {
    let el = track.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      const cs = getComputedStyle(el);
      if (/(auto|scroll|overlay)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1) {
        return el;
      }
      el = el.parentElement;
    }
    return window;
  }

  /* Hoehe des klebenden Theme-Headers. Die Header-Gruppe besteht aus
     mehreren Sections (z.B. Ankuendigungsleiste + Header) — nur die
     klebenden verdecken die Buehne. */
  function measureHeader() {
    let h = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => {
      const pos = getComputedStyle(el).position;
      if (pos !== 'sticky' && pos !== 'fixed') return;
      h += el.getBoundingClientRect().height;
    });
    h = Math.round(h);
    headerH = h;
    if (h !== lastHeader) {
      lastHeader = h;
      /* Direkter Stil auf der Buehne statt CSS-Variable: eine vererbte
         Custom Property ueber dem SVG wuerde ~10.700 Knoten neu
         berechnen (~477ms). top/height beruehrt die Kinder nicht. */
      stage.style.top = h + 'px';
      stage.style.height = h > 0 ? 'calc(100dvh - ' + h + 'px)' : '';
    }
  }

  /* Hinweis exakt zwischen Header-Ende (A) und Plakat-Oberkante (B) platzieren.
     A = Unterkante der Kopfleiste (gemessen via getBoundingClientRect)
     B = Oberkante des SVG-Inhalts (berechnet aus fit-Geometrie, relativ zur Stage)
     Hinweis-Mitte sitzt bei (A + (B-A) * percent/100) relativ zur Track.
     Wird aus measure() aufgerufen. */
  function positionHint() {
    /* A = absolute Y-Position der Header-Unterkante */
    let A = headerH;

    /* B = Plakat-Oberkante relativ zur viewBox fit-Berechnung */
    const w0 = FULL[2] * MARGIN;
    const h0 = FULL[3] * MARGIN;
    const va = stageW / stageH;
    const vbH = w0 / h0 < va ? h0 : w0 / va;
    const posterTop = stageH * (1 - FULL[3] / vbH) / 2; /* relativ zur Stage */

    /* Absolute Position von B relativ zur Track */
    const B_absolute = trackTop + posterTop;

    /* Hinweis-Position relativ zur Track */
    const hintPercent = (parseFloat(root.dataset.pzHintPosition) || 50) / 100;
    const hintY = A + (B_absolute - A) * hintPercent - trackTop;

    /* Sicherheitsabstand: Hinweis muss vollständig zwischen A und B sitzen */
    const hintHeight = 60; /* ca. Font-Hoehe + Pfeil */
    const hintTopMin = A - trackTop - hintHeight / 2;
    const hintTopMax = B_absolute - trackTop - hintHeight / 2;
    const constrained = Math.max(hintTopMin, Math.min(hintTopMax, hintY));

    hint.style.setProperty('--pz-hint-y', constrained + 'px');
  }

  /* Platz, den die klebende Kaufleiste vom unteren Rand wegnimmt.
     Auf dem Desktop ist sie eine schwebende Pille mit Abstand nach
     unten — dort ist das nicht ihre Hoehe. */
  function measureFooter() {
    const vh = window.innerHeight;
    let h = 0;
    document.querySelectorAll('[class*="sticky-add-to-cart"]').forEach((el) => {
      const cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return;
      const r = el.getBoundingClientRect();
      if (r.height < 10) return;
      if (r.top >= vh) return; /* ausgeblendet / unterhalb des Viewports */
      if (r.bottom < vh * 0.6) return; /* klebt nicht am unteren Rand */
      h = Math.max(h, vh - r.top);
    });
    h = Math.round(h);
    if (h !== lastFooter) {
      lastFooter = h;
      /* Auf die Overlays, nicht auf das Root — dort laege die vererbte
         Variable ueber dem SVG-Teilbaum. */
      overlays.style.setProperty('--pz-footer', h + 'px');
    }
  }

  /* Einzige Stelle, die Layout liest. Nie aus dem Scroll-Handler heraus. */
  function measure() {
    measureHeader();
    measureFooter();

    const next = detectScroller();
    if (next !== scroller) {
      if (scroller) scroller.removeEventListener('scroll', onScroll);
      scroller = next;
      scroller.addEventListener('scroll', onScroll, { passive: true });
    }

    stickyTop = parseFloat(getComputedStyle(stage).top) || 0;
    stageW = stage.offsetWidth || 1;
    stageH = stage.offsetHeight || 1;
    max = track.offsetHeight - stageH;

    const r = track.getBoundingClientRect();
    if (scroller === window) {
      trackTop = r.top + window.scrollY;
    } else {
      const sr = scroller.getBoundingClientRect();
      trackTop = r.top - sr.top - scroller.clientTop + scroller.scrollTop;
    }

    positionHint();
  }

  /* ------------------------------------------------------------------
     Scroll-Pfad — so schlank wie im Prototyp:
     liest die Scroll-Position, schreibt viewBox und Overlays. Sonst nichts.
     ------------------------------------------------------------------ */
  let lastNear = -1;
  let lastOutside = null;
  let ticking = false;

  function update() {
    ticking = false;

    const y = scroller === window ? window.scrollY : scroller.scrollTop;
    const rel = y - (trackTop - stickyTop);
    const p = max > 0 ? Math.min(Math.max(rel / max, 0), 1) : 1;

    svgEl.setAttribute('viewBox', fit(camRect(p)));

    const f = p * (ST.length - 1);
    const near = Math.round(f);
    /* Text und Punkte nur bei Wechsel schreiben — gleiche Ausgabe,
       aber keine unnoetige Stil-Invalidierung im SVG-Teilbaum. */
    if (near !== lastNear) {
      lastNear = near;
      caption.textContent = ST[near].t;
      for (let i = 0; i < dots.length; i++) dots[i].className = i === near ? 'on' : '';
    }
    caption.style.opacity = Math.max(0, 1 - 2.5 * Math.abs(f - near));
    /* Hinweis von der kleinen Vorzone am Seitenanfang bis 2% Fahrt zeigen.
       rel/p sind hier aus gecachter Geometrie — kein Layout-Read pro Frame.
       Toggle trifft nur das Hinweis-Element, nicht den SVG-Teilbaum. */
    hint.classList.toggle('is-hidden', !(rel > -stageH && p < 0.02));
    if (p < 0.02) caption.style.opacity = 0;

    /* Nach der Fahrt: Overlays ausblenden */
    const after = rel > max + stageH * 0.25;
    dotsBox.style.opacity = after ? '0' : '1';
    if (after) caption.style.opacity = 0;

    const outside = rel < 0 || after ? 'true' : 'false';
    if (outside !== lastOutside) {
      lastOutside = outside;
      overlays.dataset.outside = outside;
    }
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }

  /* ------------------------------------------------------------------
     Alles, was Layout aendert, meldet sich hier — nicht per Scroll.
     ------------------------------------------------------------------ */
  let remeasuring = false;
  function remeasure() {
    if (remeasuring) return;
    remeasuring = true;
    requestAnimationFrame(() => {
      remeasuring = false;
      measure();
      update();
    });
  }

  addEventListener('resize', remeasure);

  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(remeasure);
    ro.observe(track);
    ro.observe(stage);
    ro.observe(document.body); /* Inhalt oberhalb aendert den Track-Offset */
  }

  /* Die Kaufleiste blendet sich per Transition ein und aus. transitionend
     blubbert, ein Listener am Dokument genuegt — er feuert nur am Ende
     einer Animation, nicht pro Frame. */
  document.addEventListener(
    'transitionend',
    (e) => {
      const t = e.target;
      if (t instanceof Element && t.matches('[class*="sticky-add-to-cart"], [class*="sticky-add-to-cart"] *')) {
        measureFooter();
      }
    },
    { passive: true }
  );

  /* Sicherheitsnetz, falls die Leiste ohne Transition umschaltet. */
  if ('onscrollend' in window) {
    addEventListener('scrollend', measureFooter, { passive: true });
  }

  measure();
  update();

  /* Ab jetzt steuert update() den Hinweis (layout-frei aus gecachter
     Geometrie) — die frühe Brücke wird nicht mehr gebraucht. */
  removeEventListener('scroll', onEarlyHint, { capture: true });

  root.dataset.pzReady = 'true';
}

/* Lazy-Init: erst wenn die Section in Sichtweite kommt. */
function observePlakatZoom(root) {
  if (!('IntersectionObserver' in window)) {
    initPlakatZoom(root);
    setupSwatchesForSection(root);
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          initPlakatZoom(root);
          setupSwatchesForSection(root);
          io.disconnect();
        }
      }
    },
    { rootMargin: '200% 0px' }
  );
  io.observe(root);
}

function setupAll() {
  document.querySelectorAll('[data-pz-root]').forEach(observePlakatZoom);
}

/* ===== FARBWECHSEL-FEATURE ===== */

const svgCache = new Map();

/* Lädt SVG per fetch und cached es. Bei Fehler: konsole-Warning, kein Bruch. */
async function fetchAndCacheSvg(svgUrl) {
  if (svgCache.has(svgUrl)) return svgCache.get(svgUrl);

  try {
    const resp = await fetch(svgUrl);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const svg = await resp.text();
    svgCache.set(svgUrl, svg);
    return svg;
  } catch (err) {
    console.warn(`[plakat-zoom] SVG-Fehler (${svgUrl}):`, err.message);
    return null;
  }
}

/* Findet die variant-picker inputs und setzt eine Farbe programmatisch. */
function setVariantPickerValue(variantValue) {
  /* Horizons <variant-picker> rendert je Option ein <fieldset> mit
     radio-Inputs (oder ein <select> bei vielen Werten). Der name-Attribut
     traegt den Options-NAMEN (z. B. "Farbe-…"), nicht das Wort "option" —
     deshalb scoped auf die Komponente statt auf name*="option". */
  const pickers = document.querySelectorAll('variant-picker');
  if (pickers.length === 0) {
    console.warn('[plakat-zoom] Kein <variant-picker> gefunden');
    return false;
  }

  let matched = false;
  pickers.forEach((picker) => {
    /* Radio-Variante */
    const radios = picker.querySelectorAll('input[type="radio"]');
    for (const input of radios) {
      if (input.value === variantValue && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        matched = true;
      } else if (input.value === variantValue) {
        matched = true;
      }
    }
    /* Select-Variante */
    const selects = picker.querySelectorAll('select');
    for (const sel of selects) {
      const opt = [...sel.options].find((o) => o.value === variantValue);
      if (opt && sel.value !== variantValue) {
        sel.value = variantValue;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        matched = true;
      } else if (opt) {
        matched = true;
      }
    }
  });

  if (!matched) {
    console.warn(`[plakat-zoom] Varianten-Wert nicht gefunden: "${variantValue}"`);
  }
  return matched;
}

/* Wechselt die Plakat-Farbe, indem NUR der Inhalt des laufenden SVG
   ausgetauscht wird — nicht das SVG-Element selbst.

   WICHTIG: Die Kamera-Schleife (update()) haelt eine Closure-Referenz auf
   genau dieses svg-Element (svgEl) und schreibt jeden Frame die viewBox.
   Wuerde man das Element ersetzen, triebe die Kamera eine abgehaengte Leiche
   und das neue SVG bliebe eingefroren. Deshalb bleibt der Knoten erhalten;
   nur seine Kinder werden getauscht. Die viewBox (aktuelle Kameraposition)
   bleibt dadurch automatisch unangetastet. */
async function swapSvgInShadow(root, newSvgUrl) {
  const stage = root.querySelector('[data-pz-stage]');
  if (!stage) return;

  /* Der Shadow-Tree haengt am Host-DIV (.plakat-zoom__poster) INNERHALB der
     Stage, nicht an der Stage selbst. Fallback: SVG direkt in der Stage
     (Browser ohne attachShadow). */
  const host = stage.querySelector('.plakat-zoom__poster');
  const container = host && host.shadowRoot ? host.shadowRoot : stage;
  const liveSvg = container.querySelector('svg');
  if (!liveSvg) {
    console.warn('[plakat-zoom] Live-SVG nicht gefunden');
    return;
  }

  const newSvgText = await fetchAndCacheSvg(newSvgUrl);
  if (!newSvgText) return;

  /* Parse SVG-String zu DOM. */
  const doc = new DOMParser().parseFromString(newSvgText, 'image/svg+xml');
  if (doc.querySelector('parsererror') || doc.documentElement.nodeName !== 'svg') {
    console.warn('[plakat-zoom] SVG-Parse-Fehler');
    return;
  }

  const imported = document.importNode(doc.documentElement, true);

  /* Inhalt in-place tauschen: alte Kinder raus, neue rein. Knoten-Identitaet
     und aktuelle viewBox bleiben — die Kamera laeuft ohne Unterbrechung. */
  while (liveSvg.firstChild) liveSvg.removeChild(liveSvg.firstChild);
  while (imported.firstChild) liveSvg.appendChild(imported.firstChild);
}

/* Swatch-Klick Handler. */
function setupSwatchHandlers(root) {
  const swatches = root.querySelectorAll('[data-pz-swatch-svg]');
  if (swatches.length === 0) return;

  swatches.forEach((button) => {
    button.addEventListener('click', async (e) => {
      e.preventDefault();

      const svgUrl = button.dataset.pzSwatchSvg;
      const variantValue = button.dataset.pzSwatchVariant;

      /* SVG wechseln + aktive Swatch markieren. */
      await swapSvgInShadow(root, svgUrl);

      /* Variant-Picker setzen. */
      setVariantPickerValue(variantValue);

      /* Aktive Swatch visuell markieren. */
      swatches.forEach(s => s.classList.remove('is-active'));
      button.classList.add('is-active');
    });
  });

  /* Initial-Swatch markieren (erste oder Standard). */
  const initialSwatch = swatches[0];
  if (initialSwatch) initialSwatch.classList.add('is-active');
}

/* Swatch-Handler binden, wenn Section inizialisiert. */
function setupSwatchesForSection(root) {
  setupSwatchHandlers(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupAll);
} else {
  setupAll();
}

/* Theme-Editor: Section neu geladen / hinzugefuegt */
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  target.querySelectorAll('[data-pz-root]').forEach(observePlakatZoom);
});
